import React, { useMemo } from "react";
import cntl from "cntl";
import useAttributesManagementList from "./useAttributesManagementList";
import PlusCircleIconDarkGreen from "../../../assets/images/plusCircleIconDarkGreen.svg";
import BaseButton from "../../Buttons/BaseButton";
import editIcon from "../../../assets/images/editpencilgreen.svg";
import IconButton from "../../Buttons/IconButton";
import TertiaryButton from "../../Buttons/TertiaryButton";
import PrimaryButton from "../../Buttons/PrimaryButton";
import Table from "../../Table/Table";
import capitalizeFirstLetter from "../../../../helpers/Utilities/capitalizeFirstLetter";
import formatAttributeInputType from "../../../../helpers/Attributes/formatAttributeInputType";
import Checkbox from "../../Checkbox/Checkbox";
import AttributeTableEditCell from "./AttributeTableEditCell";
import TrashButton from "../../Buttons/TrashButton";

const mainContainerCN =
  "border border-es-light-grey p-8 pb-4 flex flex-col mt-4 bg-es-white h-full";
const inputCN =
  "checked:bg-es-green bg-transparent border-es-light-grey checked:border-es-light-grey";

const textCN = () =>
  cntl`
    text-es-normal text-es-medium-grey 
    truncate
    capitalize
    `;

const AttributesManagementList = () => {
  const {
    attributes,
    originalAttributes,
    attributesLoading,
    changesMade,
    onSave,
    onCancel,
    savingAttributes,
    isEditing,
    handleCheckbox,
    handleEditClick,
    handleAddAttribute,
    handleEditAttribute,
    handleRemoveAttribute,
  } = useAttributesManagementList();

  const attributeColumns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "edit",
        isPrimary: true,
        Cell: ({ row }) =>
          isEditing ? (
            <AttributeTableEditCell
              onClick={() => handleEditAttribute({ attribute: row?.original })}
            />
          ) : (
            <></>
          ),
        width: 40,
      },
      {
        Header: "Name",
        isPrimary: true,
        accessor: "detail",
        width: 200,
        Cell: (props) => {
          const { value = "", row } = props;
          // check if attributes list is empty
          if (!row) return <></>;
          return <p>{capitalizeFirstLetter(value)}</p>;
        },
      },
      {
        Header: "Type",
        isPrimary: true,
        accessor: "fieldType",
        width: 200,
        Cell: ({ value, row }) => {
          const newVal = formatAttributeInputType(value, [], row?.original);
          return <p>{newVal}</p>;
        },
      },
      {
        Header: "Count",
        isPrimary: true,
        accessor: "count",
        width: 80,
        Cell: ({ value }) => {
          return <p>{value}</p>;
        },
      },
      {
        Header: "Apply To",
        width: 600,
        Cell: ({ row }) => {
          const applyTo = row?.original?.applyTo;
          return (
            <div
              style={{ width: 600 }}
              className="grid grid-cols-4 items-center"
            >
              <div className="col-span-1 col-start-1">
                <Checkbox
                  disabled={!isEditing}
                  label="Assets"
                  labelClassName={textCN()}
                  inputClassName={inputCN}
                  size={6}
                  checked={applyTo?.asset}
                  onChange={(val) =>
                    handleCheckbox({
                      value: val,
                      type: "asset",
                      id: row?.original?.id,
                    })
                  }
                />
              </div>
              <div className="col-span-1 col-start-2">
                <Checkbox
                  disabled={!isEditing}
                  label="Properties"
                  labelClassName={textCN()}
                  inputClassName={inputCN}
                  size={6}
                  checked={applyTo?.property}
                  onChange={(val) =>
                    handleCheckbox({
                      value: val,
                      type: "property",
                      id: row?.original?.id,
                    })
                  }
                />
              </div>
              <div className="col-span-1 col-start-3">
                <Checkbox
                  disabled={!isEditing}
                  label="Spaces"
                  labelClassName={textCN()}
                  inputClassName={inputCN}
                  size={6}
                  checked={applyTo?.space}
                  onChange={(val) =>
                    handleCheckbox({
                      value: val,
                      type: "space",
                      id: row?.original?.id,
                    })
                  }
                />
              </div>
              {row?.original?.count === 0 && isEditing && (
                <div className="col-span-1 col-start-4 justify-center">
                  <TrashButton
                    onClick={() =>
                      handleRemoveAttribute({ id: row?.original?.id })
                    }
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ];
  }, [handleCheckbox, handleEditAttribute, handleRemoveAttribute, isEditing]);

  return (
    <div
      style={{ borderRadius: "10px", minWidth: "1000px" }}
      className={mainContainerCN}
    >
      {/* Header */}
      <div className="flex flex-row items-center justify-between pb-8">
        <p className="text-es-black font-es-semibold text-es-lg">
          Set Defaults
        </p>
        {!isEditing ? (
          <IconButton
            imgClassName="w-6 h-6"
            iconClassName="w-6 h-6"
            onClick={() => handleEditClick()}
            icon={editIcon}
            disabled={attributesLoading || savingAttributes}
          />
        ) : (
          <div className="flex">
            <TertiaryButton
              title="Cancel"
              className="mr-2 rounded-lg"
              cancelButton
              disabled={savingAttributes}
              onClick={onCancel}
            />
            <PrimaryButton
              disabled={changesMade || attributesLoading || savingAttributes}
              saveButton
              onClick={onSave}
            />
          </div>
        )}
      </div>
      <div className={attributesLoading && "loading w-fit h-fit z-50"}>
        <Table
          leftSearch
          resourceName="attribute"
          data={isEditing ? attributes : originalAttributes}
          columns={attributeColumns}
          cardGrid={false}
          hideCreateNewButton
          hideSiteHeaderActionButtons
          hideSiteHeaderTitle
          hideSiteHeader
          showConstantRowIndex
          showSearch
          disableDragging
          disableSorting
          hideChangeView
          hideSaveButton
          hideGroupedFooter
          hideSwitchView
          hideSiteHeaderDropdownContainer
          disableCreateTemplate
          defaultColumn={{ minWidth: 40 }}
        />
      </div>

      {isEditing && (
        <div className="py-2 flex flex-row items-center w-full border-t border-es-medium-grey mt-2">
          <BaseButton
            title="Add Attribute"
            onClick={() => handleAddAttribute()}
            className="text-es-green py-4 text-es-normal font-es-semibold"
          />
          <img
            className="h-4 w-4"
            src={PlusCircleIconDarkGreen}
            alt="plus circle icon green"
          />
        </div>
      )}
    </div>
  );
};

export default AttributesManagementList;
